import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Box, Stack, CircularProgress, Alert, List, ListItemButton, ListItemText,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Radio, RadioGroup, FormControlLabel, Divider, TextField, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { getPatients, mergePatients } from '../services/api';
import { useNotification } from '../hooks/useNotification';

// Buscador de un paciente — mismo patrón de debounce que GlobalSearch.js.
// No filtra por DNI: antes la pantalla de "elegir pares" venía de
// GET /patients/duplicates (agrupado por DNI repetido), pero como
// create/update de paciente ya bloquean DNI duplicado entre activos, esa
// detección automática dejó de tener sentido — pedido explícito del
// usuario (2026-11-04): la unificación ahora es siempre a mano, buscando
// por nombre/teléfono/DNI como cualquier otra búsqueda de paciente.
const PatientPicker = ({ label, excludeId, onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (query.length < 2) { setResults([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await getPatients({ search: query, limit: 8 });
        setResults((res.data?.items || []).filter((p) => p.id !== excludeId));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [query, excludeId]);

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <TextField
        fullWidth
        size="small"
        label={label}
        placeholder="Buscar por nombre, teléfono o DNI..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          endAdornment: loading ? <CircularProgress size={16} /> : null,
        }}
      />
      {results.length > 0 && (
        <List disablePadding sx={{ mt: 0.5, border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 220, overflow: 'auto' }}>
          {results.map((p) => (
            <ListItemButton key={p.id} onClick={() => { onSelect(p); setQuery(''); setResults([]); }}>
              <ListItemText
                primary={p.name}
                secondary={[p.dni && `DNI ${p.dni}`, p.phone].filter(Boolean).join(' · ')}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
};

// Campos comparables al fusionar — mismo set que PatientUpdate en el
// backend (ver schemas.py), sin assigned_doctor_id/is_active (no tiene
// sentido "elegir" esos dos campo por campo en esta pantalla).
const COMPARABLE_FIELDS = [
  ['first_name', 'Nombre'],
  ['last_name', 'Apellido'],
  ['phone', 'Teléfono'],
  ['email', 'Email'],
  ['address', 'Dirección'],
  ['gender', 'Sexo'],
  ['marital_status', 'Estado civil'],
  ['blood_type', 'Grupo sanguíneo'],
  ['allergies', 'Alergias'],
  ['chronic_diseases', 'Enfermedades crónicas'],
  ['current_medications', 'Medicación'],
  ['insurance_provider', 'Obra social'],
  ['insurance_policy_number', 'N° afiliado'],
  ['emergency_contact_name', 'Contacto de emergencia'],
  ['emergency_contact_phone', 'Teléfono de emergencia'],
  ['emergency_contact_relationship', 'Relación de emergencia'],
  ['permanent_notes', 'Notas permanentes'],
];

const emptyLabel = (v) => (v === null || v === undefined || v === '' ? '— (vacío)' : String(v));

// Diálogo de 2 pantallas: buscar y elegir 2 pacientes a mano -> comparación
// campo por campo entre ambos con radio buttons, confirmando qué paciente
// se conserva y qué valor queda en cada campo donde difieren. Pedido
// explícito del usuario: nunca resolver conflictos de datos solo, siempre
// que el staff elija.
const MergeDuplicatesDialog = ({ open, onClose, onMerged, initialPatients }) => {
  const notify = useNotification();
  const [picked, setPicked] = useState([]); // hasta 2 pacientes elegidos a mano
  const [keepId, setKeepId] = useState(null);
  const [choices, setChoices] = useState({}); // { field: patientId elegido }
  const [merging, setMerging] = useState(false);

  // Preseleccionar: si solo uno de los dos tiene el campo completo, ese
  // valor queda elegido de entrada — el usuario solo tiene que decidir en
  // los campos donde realmente hay un conflicto real (ambos con datos
  // distintos).
  const prefillChoices = (p1, p2) => {
    const initialChoices = {};
    for (const [field] of COMPARABLE_FIELDS) {
      const v1 = p1[field];
      const v2 = p2[field];
      if (v1 === v2) continue;
      if (!v1 && v2) initialChoices[field] = p2.id;
      else if (v1 && !v2) initialChoices[field] = p1.id;
      // si ambos tienen valor y difieren, queda sin elegir a propósito
    }
    return initialChoices;
  };

  useEffect(() => {
    if (!open) {
      setPicked([]);
      setKeepId(null);
      setChoices({});
    } else if (initialPatients?.length === 2) {
      // Se abre ya con 2 pacientes elegidos — caso "reactivar chocó con un
      // DNI activo", donde la fusión es la acción sugerida y no tiene
      // sentido hacer buscar de nuevo a alguien que ya identificó al par.
      const [p1, p2] = initialPatients;
      setPicked(initialPatients);
      setKeepId(p1.id);
      setChoices(prefillChoices(p1, p2));
    }
  }, [open, initialPatients]);

  const pickPatient = (patient) => {
    const next = [...picked, patient];
    setPicked(next);
    if (next.length === 2) {
      const [p1, p2] = next;
      setKeepId(p1.id);
      setChoices(prefillChoices(p1, p2));
    }
  };

  const removePicked = (id) => setPicked((prev) => prev.filter((p) => p.id !== id));

  const backToPicker = () => {
    setPicked([]);
    setKeepId(null);
    setChoices({});
  };

  if (picked.length < 2) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Unificar pacientes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Buscá los dos pacientes que querés unificar. Útil cuando el mismo paciente quedó cargado dos veces con datos distintos (ej. sin DNI la primera vez).
          </Typography>
          <Stack spacing={2}>
            {picked.map((p) => (
              <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[p.dni && `DNI ${p.dni}`, p.phone].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Button size="small" onClick={() => removePicked(p.id)} startIcon={<CloseIcon fontSize="small" />}>Quitar</Button>
              </Box>
            ))}
            {picked.length < 2 && (
              <PatientPicker
                label={picked.length === 0 ? 'Primer paciente' : 'Segundo paciente'}
                excludeId={picked[0]?.id}
                onSelect={pickPatient}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const [p1, p2] = picked;
  const conflicts = COMPARABLE_FIELDS.filter(([field]) => p1[field] !== p2[field]);
  const unresolvedConflicts = conflicts.filter(([field]) => p1[field] && p2[field] && !choices[field]);
  const dropId = keepId === p1.id ? p2.id : p1.id;

  const handleChoice = (field, patientId) => {
    setChoices((prev) => ({ ...prev, [field]: patientId }));
  };

  const handleMerge = async () => {
    setMerging(true);
    try {
      const chosenFields = {};
      for (const [field] of conflicts) {
        const chosenPatientId = choices[field];
        if (!chosenPatientId) continue;
        const chosenPatient = chosenPatientId === p1.id ? p1 : p2;
        chosenFields[field] = chosenPatient[field];
      }
      await mergePatients(keepId, dropId, chosenFields);
      notify('Pacientes unificados correctamente', 'success');
      onMerged?.();
      onClose();
      backToPicker();
    } catch (err) {
      notify(err.userMessage || 'No se pudo unificar', 'error');
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Unificar pacientes</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Elegí qué ficha se conserva y, en los campos donde difieren, qué valor queda. La otra ficha quedará desactivada (no se borra, sigue en el historial).
        </Typography>

        <RadioGroup value={keepId} onChange={(e) => setKeepId(Number(e.target.value))} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2}>
            {[p1, p2].map((p) => (
              <FormControlLabel
                key={p.id}
                value={p.id}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{p.name} {p.id === keepId && '(se conserva)'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.appointments_count || 0} turno(s) · {p.records_count || 0} consulta(s)
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Stack>
        </RadioGroup>

        <Divider sx={{ mb: 2 }} />

        <TableContainer sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Campo</TableCell>
                <TableCell>{p1.name}</TableCell>
                <TableCell>{p2.name}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conflicts.map(([field, label]) => {
                const needsChoice = p1[field] && p2[field];
                return (
                  <TableRow key={field}>
                    <TableCell>{label}</TableCell>
                    {[p1, p2].map((p) => (
                      <TableCell key={p.id}>
                        {needsChoice ? (
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Radio
                                size="small"
                                checked={choices[field] === p.id}
                                onChange={() => handleChoice(field, p.id)}
                              />
                            }
                            label={emptyLabel(p[field])}
                          />
                        ) : (
                          <Typography variant="body2" color={p[field] ? 'text.primary' : 'text.disabled'}>
                            {emptyLabel(p[field])}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
              {conflicts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary">No hay campos en conflicto — los datos cargados coinciden o uno de los dos está vacío.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {unresolvedConflicts.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Faltan elegir {unresolvedConflicts.length} campo(s): {unresolvedConflicts.map(([, label]) => label).join(', ')}.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={backToPicker}>Volver</Button>
        <Button
          variant="contained"
          disabled={unresolvedConflicts.length > 0 || merging}
          onClick={handleMerge}
          startIcon={merging ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Unificar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeDuplicatesDialog;
